import {
  useMutation,
  useQueryClient,
  UseMutateFunction,
} from "@tanstack/react-query";

import { VenuePagesRow } from "@punchup/venues/data/venue_pages";
import axios, { AxiosResponse } from "axios";
import { toast } from "@punchup/ui-common/shadcn/ui/use-toast";
import { useTixologi } from "@punchup/data/legacy/hooks";
import { tixologiUrl } from "../../api-base";
import type {
  BoxOfficeFeeChannel,
  BoxOfficeFeeType,
} from "../../models/box-office-fee";
import { BOX_OFFICE_FEE_QUERY_KEY } from "./useBoxOfficeFee";

/** PUT /partners/box-office-fee body — creates or replaces the venue's one default fee. */
export type UpdateBoxOfficeFeeParams = {
  name: string;
  amount: number;
  type: BoxOfficeFeeType;
  channel: BoxOfficeFeeChannel;
  taxable: boolean;
};

type UseUpdateBoxOfficeFeeReturn = {
  mutate: UseMutateFunction<
    AxiosResponse,
    Error,
    UpdateBoxOfficeFeeParams,
    unknown
  >;
  isPending: boolean;
  isSuccess: boolean;
  error: Error | null;
};

export function useUpdateBoxOfficeFee({
  venuePage,
}: {
  venuePage: VenuePagesRow;
}): UseUpdateBoxOfficeFeeReturn {
  const { tixologiData } = useTixologi({
    id: venuePage.id,
    isComedian: false,
  });
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, error } = useMutation({
    mutationFn: async (
      body: UpdateBoxOfficeFeeParams
    ): Promise<AxiosResponse> => {
      return axios.put(tixologiUrl("/partners/box-office-fee"), body, {
        headers: { Authorization: `Bearer ${tixologiData?.token}` },
      });
    },
    onSuccess: () => {
      toast({ title: "Box office fee saved", variant: "success" });
      queryClient.invalidateQueries({
        queryKey: [BOX_OFFICE_FEE_QUERY_KEY, venuePage.id],
        refetchType: "active",
      });
    },
    onError: (error) => {
      const serverMessage = axios.isAxiosError(error)
        ? error.response?.data
        : undefined;
      toast({
        title: "Error saving the box office fee",
        description:
          (typeof serverMessage === "string" && serverMessage) ||
          error.message ||
          "Something went wrong",
        variant: "destructive",
      });
    },
  });

  return { mutate, isPending, isSuccess, error };
}
